import { motion, AnimatePresence } from 'framer-motion';

export default function PagePreloader({ isVisible }) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="page-preloader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5, ease: 'easeInOut' } }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
          // The page colour of the current theme, so a light-theme visitor is not
          // flashed a black screen between two light pages.
          style={{ background: 'rgb(var(--zinc-950))' }}
        >
          {/* Subtle grain */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
            }}
          />

          {/* Radial vignette */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 50%, transparent 30%, rgb(var(--zinc-950)) 100%)' }}
          />

          {/* Breathing ambient glow */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute pointer-events-none"
            style={{
              width: 560,
              height: 560,
              borderRadius: '50%',
              background: 'radial-gradient(ellipse at center, rgba(249,115,22,0.22) 0%, transparent 65%)',
            }}
          />

          {/* Ring pulse — wave 1 */}
          <motion.div
            initial={{ scale: 0.4, opacity: 0.6 }}
            animate={{ scale: 2.8, opacity: 0 }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
            className="absolute pointer-events-none rounded-full border border-orange-500/25"
            style={{ width: 160, height: 160 }}
          />
          {/* Ring pulse — wave 2 (staggered) */}
          <motion.div
            initial={{ scale: 0.4, opacity: 0.45 }}
            animate={{ scale: 2.8, opacity: 0 }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut', delay: 0.75 }}
            className="absolute pointer-events-none rounded-full border border-orange-400/15"
            style={{ width: 160, height: 160 }}
          />

          {/* Logo + wordmark */}
          <div className="relative flex flex-col items-center select-none">
            <motion.img
              src="/images/builderslab-logo.png"
              alt="BuildersLab"
              initial={{ scale: 0.72, opacity: 0, filter: 'blur(14px)' }}
              animate={{
                scale:   [0.72, 1.0,  1.05, 1.0,  1.14],
                opacity: [0,    1,    1,    1,    0  ],
                filter:  [
                  'blur(14px)',
                  'blur(0px)',
                  'blur(0px)',
                  'blur(0px)',
                  'blur(6px)',
                ],
              }}
              transition={{
                duration: 2.9,
                times: [0, 0.2, 0.48, 0.72, 1],
                ease: 'easeInOut',
              }}
              style={{
                height: 100,
                width: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 48px rgba(249,115,22,0.55))',
              }}
            />

            <motion.span
              initial={{ opacity: 0, y: 8, letterSpacing: '0.15em' }}
              animate={{
                opacity: [0, 1, 1, 0],
                y: [8, 0, 0, -4],
                letterSpacing: ['0.15em', '0.28em', '0.28em', '0.34em'],
              }}
              transition={{ duration: 2.9, times: [0, 0.26, 0.72, 1] }}
              className="mt-6 text-white/70 text-xs font-bold uppercase"
            >
              BuildersLab
            </motion.span>
          </div>

          {/* Bottom progress line */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/5">
            <motion.div
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 2.95, ease: 'linear' }}
              style={{
                height: '100%',
                background: 'linear-gradient(90deg, #ea580c, #fb923c, #ea580c)',
                backgroundSize: '200% 100%',
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
